import { useReducedMotion as useFramerReducedMotion } from 'framer-motion';

/**
 * Hook that provides motion-safe animation props.
 * When the user has "Reduce motion" enabled in their OS,
 * all animations resolve instantly (duration: 0, no delays).
 *
 * Usage:
 *   const { fadeInUp, fadeIn, stagger, scaleIn, slideDown, pageTransition, noHover } = useMotionConfig();
 *   <m.div {...fadeInUp}> ... </m.div>
 *   <m.button {...stagger(index)}> ... </m.button>
 */
export function useReducedMotion() {
  return !!useFramerReducedMotion();
}

export function useMotionConfig() {
  const prefersReducedMotion = !!useFramerReducedMotion();

  if (prefersReducedMotion) {
    return {
      // All animations resolve instantly
      fadeInUp: { initial: { opacity: 1 }, animate: { opacity: 1 } },
      fadeIn: { initial: { opacity: 1 }, animate: { opacity: 1 } },
      scaleIn: { initial: { opacity: 1, scale: 1 }, animate: { opacity: 1, scale: 1 } },
      slideDown: { initial: { opacity: 1, height: 'auto' }, animate: { opacity: 1, height: 'auto' }, exit: { opacity: 0, height: 0 } },
      slideUp: { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 } },
      dropdownIn: { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 0 } },
      pageTransition: { initial: { opacity: 1, x: 0 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0 }, transition: { duration: 0 } },
      modalOverlay: { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
      modalContent: { initial: { opacity: 1, scale: 1 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 1 }, transition: { duration: 0 } },
      chipScale: { initial: { opacity: 1, scale: 1 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 1 } },

      stagger: () => ({ initial: { opacity: 1 }, animate: { opacity: 1 } }),
      staggerScale: () => ({ initial: { opacity: 1, scale: 1 }, animate: { opacity: 1, scale: 1 } }),
      delayed: () => ({ initial: { opacity: 1 }, animate: { opacity: 1 } }),
      noHover: {},
      noTap: {},
    };
  }

  return {
    fadeInUp: { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } },
    fadeIn: { initial: { opacity: 0 }, animate: { opacity: 1 } },
    scaleIn: { initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 } },
    slideDown: { initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: 'auto' }, exit: { opacity: 0, height: 0 } },
    slideUp: { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -20 } },
    dropdownIn: { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 } },
    pageTransition: { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -20 }, transition: { duration: 0.3 } },
    modalOverlay: { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
    modalContent: { initial: { scale: 0.9, opacity: 0 }, animate: { scale: 1, opacity: 1 }, exit: { scale: 0.9, opacity: 0 }, transition: { type: 'spring', damping: 25, stiffness: 300 } },
    chipScale: { initial: { opacity: 0, scale: 0.8 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.8 }, transition: { duration: 0.2 } },

    stagger: (index, interval = 0.1) => ({
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { delay: index * interval },
    }),
    staggerScale: (index, interval = 0.05) => ({
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
      transition: { delay: index * interval },
    }),
    delayed: (delay) => ({
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { delay },
    }),
    noHover: { whileHover: { y: -5 } },
    noTap: { whileTap: { scale: 0.98 } },
  };
}
